<template>
    <validation-observer ref="rate_season_form" v-slot="{ meta }">
        <modal
            :id="modalId"
            :title="title"
            @ok.prevent="createEditObject"
            @shown="initObject"
            body-class="create-edit-rate-season-modal"
        >
            <busy-modal :show="loading" />
            <div class="rate-season-form" v-if="values">
                <b-row>
                    <b-col md="8" class="pr-md-0">
                        <validation-provider name="name" v-slot="{ errors }" rules="required">
                            <form-input v-model="values.name" :error="errors[0]" label="Name: " />
                        </validation-provider>
                    </b-col>
                    <b-col md="4">
                        <validation-provider
                            name="min_nights"
                            v-slot="{ errors }"
                            rules="validPositiveInteger|maxValue:1000|required"
                        >
                            <form-input v-model="values.min_nights" :error="errors[0]" label="Min Nights:" />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="!(isDefault && editMode)">
                    <b-col md="6" class="pr-md-0">
                        <validation-provider name="period.from_date" v-slot="{ errors }" rules="required">
                            <form-datepicker
                                v-model="values.period.from_date"
                                label="From Date:"
                                :hide-header="true"
                                :display-format="userUnicodeDateFormat"
                                :date-disabled-fn="dateDisabled"
                                :min="minDate"
                                :max="maxDate"
                                :error="errors[0]"
                                :initial-date="initFromDate"
                                class="mb-1"
                                @input="onChangeDate"
                            />
                            <small class="text-danger d-block" v-if="isNegativePeriod">
                                End date must be after start date.
                            </small>
                            <small class="text-danger d-block" v-if="isOverlapping && !isNegativePeriod">
                                Dates overlap an existing period.
                            </small>
                        </validation-provider>
                    </b-col>
                    <b-col md="6">
                        <validation-provider name="period.to_date" v-slot="{ errors }" rules="required">
                            <form-datepicker
                                v-if="renderComponent"
                                v-model="values.period.to_date"
                                label="To Date:"
                                :hide-header="true"
                                :display-format="userUnicodeDateFormat"
                                :date-disabled-fn="dateDisabled"
                                :min="minDate"
                                :max="maxDate"
                                :error="errors[0]"
                                :initial-date="initToDate"
                                ref="to_date"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="6" class="pr-md-0">
                        <validation-provider name="daily_rate" v-slot="{ errors }" rules="validPositiveFloat|required">
                            <form-input
                                v-model="values.daily_rate"
                                :error="errors[0]"
                                label="Nightly Rate"
                                placeholder="0.00"
                            />
                        </validation-provider>
                    </b-col>
                    <b-col md="6">
                        <validation-provider
                            name="weekend_rate"
                            v-slot="{ errors }"
                            :rules="values.weekend_rate_enabled ? 'validPositiveFloat|required' : ''"
                        >
                            <form-input
                                v-show="values.weekend_rate_enabled"
                                label="Weekend Rate"
                                v-model="values.weekend_rate"
                                :leftButton="true"
                                :error="errors[0]"
                                placeholder="0.00"
                            >
                                <template #leftButton>
                                    <form-checkbox v-model="values.weekend_rate_enabled" class="mb-0" />
                                </template>
                            </form-input>
                            <form-input
                                v-show="!values.weekend_rate_enabled"
                                label="Weekend Rate"
                                value="N/A"
                                :leftButton="true"
                                :disabled="true"
                                placeholder="0.00"
                            >
                                <template #leftButton>
                                    <form-checkbox
                                        v-model="values.weekend_rate_enabled"
                                        class="mb-0"
                                        @change="setDefaultWeekendRate"
                                    />
                                </template>
                            </form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="rateSettings && (rateSettings.use_weekly_rates || rateSettings.use_monthly_rates)">
                    <b-col md="6" class="pr-md-0">
                        <validation-provider name="weekly_rate" v-slot="{ errors }" :rules="weeklyRateRules">
                            <form-input
                                v-show="values.weekly_rate_enabled"
                                label="Weekly Rate"
                                v-model="values.weekly_rate"
                                :leftButton="true"
                                :error="errors[0]"
                                placeholder="0.00"
                            >
                                <template #leftButton>
                                    <form-checkbox
                                        v-model="values.weekly_rate_enabled"
                                        class="mb-0"
                                        :disabled="!rateSettings.use_weekly_rates || isDefault"
                                    />
                                </template>
                            </form-input>
                            <form-input
                                v-show="!values.weekly_rate_enabled"
                                label="Weekly Rate"
                                :leftButton="true"
                                :disabled="true"
                                :placeholder="$parent.getWeeklyRatesPlaceholder(true)"
                            >
                                <template #leftButton>
                                    <form-checkbox
                                        v-model="values.weekly_rate_enabled"
                                        class="mb-0"
                                        :disabled="!rateSettings.use_weekly_rates || isDefault"
                                        @change="setDefaultWeeklyRate"
                                    />
                                </template>
                            </form-input>
                        </validation-provider>
                    </b-col>
                    <b-col md="6">
                        <validation-provider name="monthly_rate" v-slot="{ errors }" :rules="monthlyRateRules">
                            <form-input
                                v-show="values.monthly_rate_enabled"
                                label="Monthly Rate"
                                v-model="values.monthly_rate"
                                :leftButton="true"
                                :error="errors[0]"
                                placeholder="0.00"
                            >
                                <template #leftButton>
                                    <form-checkbox
                                        v-model="values.monthly_rate_enabled"
                                        class="mb-0"
                                        :disabled="!rateSettings.use_monthly_rates || isDefault"
                                    />
                                </template>
                            </form-input>
                            <form-input
                                v-show="!values.monthly_rate_enabled"
                                label="Monthly Rate"
                                :leftButton="true"
                                :disabled="true"
                                :placeholder="$parent.getMonthlyRatesPlaceholder(true)"
                            >
                                <template #leftButton>
                                    <form-checkbox
                                        v-model="values.monthly_rate_enabled"
                                        class="mb-0"
                                        :disabled="!rateSettings.use_monthly_rates || isDefault"
                                        @change="setDefaultMonthlyRate"
                                    />
                                </template>
                            </form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row
                    v-if="
                        ruleSettings &&
                        (ruleSettings.arrival_day_rule_enabled || ruleSettings.departure_day_rule_enabled)
                    "
                >
                    <b-col md="6" class="pr-md-0">
                        <validation-provider
                            name="arrival_day"
                            v-slot="{ errors }"
                            :rules="values.arrival_day_enabled ? 'required' : ''"
                        >
                            <form-select
                                v-show="values.arrival_day_enabled"
                                label="Arrival Day"
                                v-model="values.arrival_day"
                                :leftButton="true"
                                :error="errors[0]"
                                :options="daysOptions"
                            >
                                <template #leftButton>
                                    <form-checkbox
                                        v-model="values.arrival_day_enabled"
                                        class="mb-0"
                                        :disabled="!ruleSettings.arrival_day_rule_enabled"
                                    />
                                </template>
                            </form-select>
                            <form-input
                                v-show="!values.arrival_day_enabled"
                                label="Arrival Day"
                                value="N/A"
                                :leftButton="true"
                                :disabled="true"
                            >
                                <template #leftButton>
                                    <form-checkbox
                                        v-model="values.arrival_day_enabled"
                                        class="mb-0"
                                        :disabled="!ruleSettings.arrival_day_rule_enabled"
                                    />
                                </template>
                            </form-input>
                        </validation-provider>
                    </b-col>
                    <b-col md="6">
                        <validation-provider
                            name="departure_day"
                            v-slot="{ errors }"
                            :rules="values.departure_day_enabled ? 'required' : ''"
                        >
                            <form-select
                                v-show="values.departure_day_enabled"
                                label="Departure Day"
                                v-model="values.departure_day"
                                :leftButton="true"
                                :error="errors[0]"
                                :options="daysOptions"
                            >
                                <template #leftButton>
                                    <form-checkbox
                                        v-model="values.departure_day_enabled"
                                        class="mb-0"
                                        :disabled="!ruleSettings.departure_day_rule_enabled"
                                    />
                                </template>
                            </form-select>
                            <form-input
                                v-show="!values.departure_day_enabled"
                                label="Departure Day"
                                value="N/A"
                                :leftButton="true"
                                :disabled="true"
                            >
                                <template #leftButton>
                                    <form-checkbox
                                        v-model="values.departure_day_enabled"
                                        class="mb-0"
                                        :disabled="!ruleSettings.departure_day_rule_enabled"
                                    />
                                </template>
                            </form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="rateSettings && rateSettings.use_per_person_rates">
                    <b-col md="6" class="pr-md-0">
                        <validation-provider
                            name="extra_person_base"
                            v-slot="{ errors }"
                            :rules="values.extra_person_rate_enabled ? 'required' : ''"
                        >
                            <form-select
                                v-show="values.extra_person_rate_enabled"
                                label="Base Guests:"
                                v-model="values.extra_person_base"
                                :leftButton="true"
                                :error="errors[0]"
                                :options="maxGuestsOptions"
                            >
                                <template #leftButton>
                                    <form-checkbox
                                        v-model="values.extra_person_rate_enabled"
                                        class="mb-0"
                                        :disabled="!rateSettings.use_per_person_rates"
                                    />
                                </template>
                            </form-select>
                            <form-input
                                v-show="!values.extra_person_rate_enabled"
                                label="Base Guests:"
                                value="N/A"
                                :leftButton="true"
                                :disabled="true"
                            >
                                <template #leftButton>
                                    <form-checkbox
                                        v-model="values.extra_person_rate_enabled"
                                        class="mb-0"
                                        :disabled="!rateSettings.use_per_person_rates"
                                    />
                                </template>
                            </form-input>
                        </validation-provider>
                    </b-col>
                    <b-col md="6">
                        <validation-provider name="extra_person_rate" v-slot="{ errors }" rules="validFloat">
                            <form-input
                                label="Addtl Guest Rate:"
                                v-model="values.extra_person_rate"
                                :error="errors[0]"
                                :disabled="!values.extra_person_rate_enabled"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="isSeasonality">
                    <b-col md="12">
                        <validation-provider name="delta" v-slot="{ errors }" rules="required|validFloat">
                            <form-input label="Delta:" v-model="values.delta" :error="errors[0]" />
                        </validation-provider>
                    </b-col>
                </b-row>
            </div>
            <template #modal-footer="{ cancel, ok }">
                <b-button v-if="!isDefault && editMode" variant="danger" @click="onDelete">Delete</b-button>
                <b-button v-if="!isDefault && !editMode" variant="default" @click="onReset">Reset</b-button>
                <b-button variant="warning" @click="cancel()">Close</b-button>
                <b-button variant="primary" :disabled="loading || !meta.valid || isOverlapping || isNegativePeriod" @click="ok"
                    >Save</b-button
                >
                <b-button variant="primary" @click="logErrors" v-if="false">LOG</b-button>
            </template>
        </modal>
    </validation-observer>
</template>

<script>
    import isNil from 'lodash/isNil';
    import Modal from '../generic/modal/modal.vue';
    import BusyModal from '../generic/busy-modal/busy-modal.vue';
    import FormInput from '../generic/form-input/form-input.vue';
    import FormCheckbox from '../generic/form-checkbox/form-checkbox.vue';
    import FormDatepicker from '../generic/form-datepicker/form-datepicker.vue';
    import FormSelect from '../generic/form-select/form-select.vue';
    import { mapGetters } from 'vuex';
    export default {
        components: {
            Modal,
            BusyModal,
            FormInput,
            FormDatepicker,
            FormCheckbox,
            FormSelect,
        },
        data() {
            return {
                values: null,
                renderComponent: true,
            };
        },
        props: {
            modalId: String,
            title: String,
            objects: Array,
            rateSettings: Object,
            currentYear: Number,
            ruleSettings: Object,
            maxGuests: Number,
            rateId: Number,
            initValues: Object,
            newObject: Object,
            isDefault: Boolean,
            loading: Boolean,
            editMode: Boolean,
            isSeasonality: {
                type: Boolean,
                default: false,
            },
            lastObject: {
                type: Object,
                default: () => null,
                required: false,
            },
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
            minDate() {
                return new Date(this.currentYear, 0, 1);
            },
            maxDate() {
                return new Date(this.currentYear, 11, 31);
            },
            initFromDate() {
                return new Date(this.currentYear, 0, 1);
            },
            initToDate() {
                return this.values && this.values.period.from_date
                    ? this.generateDate(this.values.period.from_date)
                    : this.initFromDate;
            },
            maxGuestsOptions() {
                return Array.from({ length: this.maxGuests }, (v, k) => k + 1);
            },
            weeklyRateRules() {
                if (this.isSeasonality && this.values.default) {
                    return this.values.weekly_rate_enabled ? 'validPositiveOrZeroFloat|required' : '';
                } else {
                    return this.values.weekly_rate_enabled ? 'validPositiveFloat|required' : '';
                }
            },
            monthlyRateRules() {
                if (this.isSeasonality && this.values.default) {
                    return this.values.monthly_rate_enabled ? 'validPositiveOrZeroFloat|required' : '';
                } else {
                    return this.values.monthly_rate_enabled ? 'validPositiveFloat|required' : '';
                }
            },
            daysOptions() {
                return [
                    {
                        value: 6,
                        text: 'Sunday',
                    },
                    {
                        value: 0,
                        text: 'Monday',
                    },
                    {
                        value: 1,
                        text: 'Tuesday',
                    },
                    {
                        value: 2,
                        text: 'Wednesday',
                    },
                    {
                        value: 3,
                        text: 'Thursday',
                    },
                    {
                        value: 4,
                        text: 'Friday',
                    },
                    {
                        value: 5,
                        text: 'Saturday',
                    },
                ];
            },
            isNegativePeriod() {
                if (this.values && this.values.period) {
                    if (this.values.period.from_date && this.values.period.to_date) {
                        if (
                            this.generateDate(this.values.period.from_date).getTime() >
                            this.generateDate(this.values.period.to_date).getTime()
                        ) {
                            return true;
                        }
                    }
                }
            },
            isOverlapping() {
                if (this.values && this.values.period) {
                    if (this.values.period.from_date == null || this.values.period.to_date == null) return;
                    const initFromDate = this.generateDate(this.values.period.from_date);
                    const from_date = Date.UTC(
                        initFromDate.getFullYear(),
                        initFromDate.getMonth(),
                        initFromDate.getDate()
                    );
                    const initToDate = this.generateDate(this.values.period.to_date);
                    const to_date = Date.UTC(initToDate.getFullYear(), initToDate.getMonth(), initToDate.getDate());
                    for (let i = 0; i < this.objects.length; i++) {
                        if (this.objects[i].period.from_date != null && this.objects[i].id != this.rateId) {
                            if (
                                this.objects[i].period.from_timestamp <= to_date &&
                                this.objects[i].period.to_timestamp >= from_date
                            ) {
                                return true;
                            }
                        }
                    }
                }
            },
        },
        methods: {
            onChangeDate() {
                this.forceRerender();
            },
            generateDate(dateString) {
                if (dateString) {
                    const datesArr = dateString.split('-');
                    return new Date(datesArr[0], datesArr[1] - 1, datesArr[2]);
                }
                return null;
            },
            // use this function for re-render datepicker, because initial-date props is not reactive
            forceRerender() {
                this.renderComponent = false;
                this.$nextTick(() => {
                    this.renderComponent = true;
                });
            },
            async createEditObject() {
                this.$emit('submit', this.values);
            },
            initObject() {
                if (isNil(this.values)) {
                    this.values = JSON.parse(JSON.stringify(this.newObject));
                }
                if (this.values.id == 0) {
                    if (isNil(this.lastObject)) {
                        this.values.min_nights = this.objects[0].min_nights;
                    } else {
                        const period = this.values.period;
                        this.values = JSON.parse(JSON.stringify(this.lastObject));
                        this.values.id = 0;
                        this.values.period = period;
                        this.values.default = false;
                        this.values.name = this.isSeasonality ? 'New Season' : 'New Rate';
                    }
                }
                this.$refs.rate_season_form.setValues(this.values);
            },
            dateDisabled(_, date) {
                if (date) {
                    const day = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
                    for (let i = 0; i < this.objects.length; i++) {
                        if (this.objects[i].period.from_date != null && this.objects[i].id != this.rateId) {
                            if (
                                this.objects[i].period.from_timestamp <= day &&
                                this.objects[i].period.to_timestamp >= day
                            )
                                return true;
                        }
                    }
                }
            },
            setDefaultWeekendRate() {
                if (this.isSeasonality) {
                    this.values.weekend_rate = this.values.daily_rate;
                }
            },
            setDefaultWeeklyRate() {
                if (this.isSeasonality) {
                    if (this.values.weekly_rate <= 0) {
                        this.values.weekly_rate =
                            '' + parseFloat(Math.round(this.values.daily_rate * 7 * 100) / 100).toFixed(2);
                    }
                    if (this.values.weekly_rate == null) {
                        this.values.weekly_rate = '0.00';
                    }
                }
            },
            setDefaultMonthlyRate() {
                if (this.isSeasonality) {
                    if (this.values.monthly_rate <= 0) {
                        this.values.monthly_rate =
                            '' + parseFloat(Math.round(this.values.daily_rate * 30 * 100) / 100).toFixed(2);
                    }
                    if (this.values.monthly_rate == null) {
                        this.values.monthly_rate = '0.00';
                    }
                }
            },
            logErrors() {
                this.$refs.rate_season_form.validate().then(() => {});
            },
            onReset() {
                this.$emit('reset');
            },
            onDelete() {
                this.$emit('delete', this.values.id);
            },
        },
        watch: {
            initValues() {
                this.values = this.initValues;
            },
            values: {
                handler(val) {
                    this.$refs.rate_season_form.setValues(val);
                },
                deep: true,
            },
        },
    };
</script>

<style lang="scss">
    .create-edit-rate-season-modal {
        min-height: 300px;
    }
</style>
